import { useNavigate, useParams } from '@tanstack/react-router';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useGetProjects } from '@/api/projects/queries';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';

export const ProjectsSelect = () => {
  const [open, setOpen] = useState(false);
  const { projectId } = useParams({ strict: false }) as { projectId: string };
  const navigate = useNavigate();
  const { data } = useGetProjects();
  useHotkeys(
    !data ? '' : data?.map((_, i) => i + 1).join(','),
    (event) => {
      if (!data) {
        return;
      }

      const key = Number.parseInt(event.key, 10);
      const project = data[key - 1];

      if (!project) {
        return;
      }
      setOpen(false);
      handleProjectChange(project.id);
    },
    { enabled: open },
  );

  const handleProjectChange = (projectId: string) => {
    navigate({ to: '.', params: { projectId }, search: { status: 'pending' } });
  };

  const activeProject = data?.find(({ id }) => id === projectId);

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground justify-center border border-input"
            >
              <div className="grid flex-1 text-left text-sm leading-tight [[data-state=collapsed]_&]:hidden">
                <span className="text-xs text-muted-foreground">Project</span>

                <span className="truncate font-medium">{activeProject?.name}</span>
              </div>
              <ChevronsUpDown className="[[data-state=expanded]_&]:ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            align="start"
            side="bottom"
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              Switch project
            </DropdownMenuLabel>
            {data?.map((project, index) => (
              <DropdownMenuItem
                key={project.id}
                className="p-2 flex flex-1 flex-row"
                onClick={() => handleProjectChange(project.id)}
              >
                <div className="flex flex-1 flex-row gap-2 items-center">
                  <div className="flex-1">{project.name}</div>
                  <div className="width-4">
                    {project.id === projectId && <Check className="text-white" />}
                  </div>
                  <div className="width-4 text-muted-foreground">{index + 1}</div>
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};
